import type {QueryClient} from '@tanstack/react-query'
import {useQueryClient} from '@tanstack/react-query'
import {createContext, use, useCallback, useMemo} from 'react'

import {
  MemexColumnDataType,
  type MemexProjectColumnId,
  type SystemColumnId,
} from '../../../api/columns/contracts/memex-column'
import type {UpdateColumnValueAction} from '../../../api/columns/contracts/storage'
import type {IAssignee} from '../../../api/common-contracts'
import {AutoAssignItemStatusChangeName, AutoAssignItemStatusChangeUI} from '../../../api/stats/contracts'
import {isItemMovingWithinSortedValue} from '../../../features/sorting/is-item-moving-within-sorted-value'
import {DropSide} from '../../../helpers/dnd-kit/drop-helpers'
import {isAutoAssignAgentWorkflow} from '../../../helpers/workflow-utilities'
import {usePostStats} from '../../../hooks/common/use-post-stats'
import {useGetUpdateForGroupDropEvent} from '../../../hooks/drag-and-drop/use-group-drop-handler'
import {useBulkUpdateItems} from '../../../hooks/use-bulk-update-items'
import {useEnabledFeatures} from '../../../hooks/use-enabled-features'
import {useSortedBy} from '../../../hooks/use-sorted-by'
import {buildUpdateItemActions, useUpdateAndReorderItem} from '../../../hooks/use-update-item'
import {hasServerGroupId, type HorizontalGroup} from '../../../models/horizontal-group'
import type {MemexItemModel} from '../../../models/memex-item-model'
import type {VerticalGroup} from '../../../models/vertical-group'
import {getServerGroupIdForVerticalGroupId} from '../../../state-providers/memex-items/query-client-api/memex-groups'
import {isMemexItemInGroup} from '../../../state-providers/memex-items/query-client-api/memex-items/item-helpers'
import {
  type OptimisticUpdateRollbackData,
  rollbackMemexItemData,
  updateGroupForMemexItems,
} from '../../../state-providers/memex-items/query-client-api/memex-items/update-items'
import type {ReorderItemData} from '../../../state-providers/memex-items/types'
import {useWorkflows} from '../../../state-providers/workflows/use-workflows'
import {Resources} from '../../../strings'
import {
  getCopilotAssigneeAction,
  shouldOpenCopilotModal,
  useCopilotAssignmentModal,
} from '../../table/copilot-assignment-modal-provider'
import useToasts, {ToastType} from '../../toasts/use-toasts'
import {useBoardItems} from './use-board-items'

export const CardPositionType = {
  TOP: 'top',
  BOTTOM: 'bottom',
} as const
export type CardPositionType = ObjectValues<typeof CardPositionType>

type BoardCardActionsContextType = {
  /** Reorders a card within a column, and optionally moves it to a new column */
  moveCard: (
    cardToMove: MemexItemModel,
    previousCardId: number | undefined,
    side: 'top' | 'bottom',
    newVerticalGroup?: VerticalGroup,
    oldVerticalGroupId?: string,
    dragHorizontalGroup?: HorizontalGroup,
    dropHorizontalGroup?: HorizontalGroup,
  ) => Promise<void>
  /** Moves multiple cards to a new column */
  moveCards: (
    selectedItems: Array<MemexItemModel>,
    newVerticalGroup: VerticalGroup,
    dragHorizontalGroup?: HorizontalGroup,
    dropHorizontalGroup?: HorizontalGroup,
  ) => Promise<void>
  /** Moves a card to the top or bottom position within its current column */
  moveCardToPosition: (cardToMoveId: number, verticalGroupId: string, position: 'top' | 'bottom') => Promise<void>
}

const BoardCardActionsContext = createContext<BoardCardActionsContextType | null>(null)

export const BoardCardActionsProvider: React.FC<{
  groupByFieldId: number | typeof SystemColumnId.Status
  hasIssueFieldVerticalGroup: boolean
  horizontalGroupByFieldId: MemexProjectColumnId | undefined
  children: React.ReactNode
}> = ({groupByFieldId, hasIssueFieldVerticalGroup, horizontalGroupByFieldId, children}) => {
  const {updateAndReorderItem} = useUpdateAndReorderItem()
  const {workflowsUsingColumnOption} = useWorkflows()
  const {postStats} = usePostStats()
  const {groupedItems} = useBoardItems()
  const {bulkUpdateMultipleColumnValues} = useBulkUpdateItems()
  const {getUpdateForGroupDropEvent, handleGroupDropRequestError, validateUpdateForGroup} =
    useGetUpdateForGroupDropEvent()
  const {sorts, clearSortedBy} = useSortedBy()
  const {addToast} = useToasts()
  const queryClient = useQueryClient()
  const {tryOpenCopilotModal} = useCopilotAssignmentModal()
  const {memex_group_by_multi_value_changes} = useEnabledFeatures()

  const moveCard = useCallback(
    async (
      cardToMove: MemexItemModel,
      previousCardId: number | undefined,
      side: 'top' | 'bottom',
      newVerticalGroup?: VerticalGroup,
      oldVerticalGroupId?: string,
      dragHorizontalGroup?: HorizontalGroup,
      dropHorizontalGroup?: HorizontalGroup,
    ) => {
      // skip if we're over the same card
      if (cardToMove.id === previousCardId) {
        return
      }

      const executeMoveLogic = async (): Promise<void> => {
        const updateColumnActions: Array<UpdateColumnValueAction> = []
        let horizontalGroupActionFailed = false

        if (
          dragHorizontalGroup?.sourceObject &&
          dropHorizontalGroup?.sourceObject &&
          dragHorizontalGroup.value !== dropHorizontalGroup.value
        ) {
          // Multi-select swimlanes can't be regrouped by drag-and-drop, so a cross-swimlane drop
          // is a full no-op rather than reordering the card without changing its value.
          if (dropHorizontalGroup.sourceObject.dataType === MemexColumnDataType.MultiSelect) {
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              message: Resources.cannotDragForMultiSelectGroupMessage,
              type: ToastType.warning,
            })
            return
          }
          // Check if this is a Copilot assignment - if so, include the actor in the assignees
          if (shouldOpenCopilotModal([cardToMove], dropHorizontalGroup.sourceObject)) {
            const copilotAssigneeAction = getCopilotAssigneeAction({
              targetAssignees: dropHorizontalGroup.sourceObject.value as Array<IAssignee>,
              existingAssignees: cardToMove.columns.Assignees ?? [],
              keepExistingAssignees: memex_group_by_multi_value_changes ?? false,
            })
            updateColumnActions.push(copilotAssigneeAction)
          } else {
            const columnAction = await getUpdateForGroupDropEvent({
              activeItem: cardToMove,
              activeItemGroup: dragHorizontalGroup,
              overItemGroup: dropHorizontalGroup,
            })

            if (columnAction) {
              updateColumnActions.push(columnAction)
            } else {
              horizontalGroupActionFailed = true
            }
          }
        }

        if (newVerticalGroup && newVerticalGroup.id !== oldVerticalGroupId && !horizontalGroupActionFailed) {
          let nextAction: UpdateColumnValueAction | undefined = {
            dataType: MemexColumnDataType.SingleSelect,
            memexProjectColumnId: groupByFieldId,
            value: {id: newVerticalGroup.groupMetadata?.id ?? ''},
            isIssueField: hasIssueFieldVerticalGroup,
          }
          nextAction = await validateUpdateForGroup(cardToMove, nextAction)

          if (nextAction) {
            updateColumnActions.push(nextAction)
          } else {
            return
          }
        }

        const dropSide = side === 'top' ? DropSide.BEFORE : DropSide.AFTER
        const overGroupId = groupedItems.allItemsByVerticalGroup[newVerticalGroup?.id || '']?.groupId
        const reorderData: ReorderItemData =
          previousCardId != null
            ? {overItemId: previousCardId, side: dropSide, overGroupId}
            : {overGroupId: overGroupId || ''}
        if (hasServerGroupId(dropHorizontalGroup)) {
          reorderData.overSecondaryGroupId = dropHorizontalGroup.serverGroupId
        }

        const updateItemData = buildUpdateItemActions(updateColumnActions, reorderData)

        if (!updateItemData) {
          return
        }

        const moveIsWithinSameColumn = oldVerticalGroupId != null && oldVerticalGroupId === newVerticalGroup?.id
        const moveIsWithinSameSwimlane = dragHorizontalGroup?.sourceObject === dropHorizontalGroup?.sourceObject
        const droppingInEmptyRow = !('overItemId' in reorderData)

        // We don't want to allow a user to attempt to drag and drop an item _outside of items
        // with the same sort value_ within the same column in a sorted board view.
        if (moveIsWithinSameColumn && moveIsWithinSameSwimlane && !droppingInEmptyRow) {
          // If we have a horizontal group, we want to use the items from the cell for that swimlane/column
          // combination - otherwise, just look up the items for the column.
          const itemsInGroup =
            dropHorizontalGroup != null
              ? dropHorizontalGroup.itemsByVerticalGroup[oldVerticalGroupId]
              : groupedItems.allItemsByVerticalGroup[oldVerticalGroupId]

          if (itemsInGroup) {
            for (const sort of sorts) {
              const showToast = !isItemMovingWithinSortedValue(
                cardToMove,
                sort.column,
                itemsInGroup.items,
                reorderData.overItemId,
                reorderData.side,
              )
              if (showToast) {
                const action = {
                  text: Resources.cannotReorderForSortAction,
                  handleClick: () => clearSortedBy(),
                }

                // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
                addToast({
                  message: Resources.cannotReorderForSortMessage,
                  type: ToastType.warning,
                  action,
                })

                return
              }
            }
          }
        }

        try {
          await updateAndReorderItem(cardToMove.id, updateItemData, {layoutType: 'board'})
          // If the drag moved the card into a column whose option is the source column of
          // an enabled auto-assign workflow, emit an attribution stat.
          if (
            typeof groupByFieldId === 'number' &&
            newVerticalGroup &&
            newVerticalGroup.id !== oldVerticalGroupId &&
            newVerticalGroup.groupMetadata?.id
          ) {
            const matchingWorkflows = workflowsUsingColumnOption(groupByFieldId, newVerticalGroup.groupMetadata.id)
            if (matchingWorkflows.some(w => w.enabled && isAutoAssignAgentWorkflow(w))) {
              postStats({
                name: AutoAssignItemStatusChangeName,
                ui: AutoAssignItemStatusChangeUI.BoardColumn,
                memexProjectColumnId: groupByFieldId,
                memexProjectItemId: cardToMove.id,
              })
            }
          }
        } catch (error) {
          handleGroupDropRequestError(error)
        }
      }

      // Check if we should open the Copilot assignment modal
      const modalOpened = dropHorizontalGroup?.sourceObject
        ? tryOpenCopilotModal([cardToMove], dropHorizontalGroup.sourceObject, executeMoveLogic)
        : false
      if (modalOpened) {
        return
      }

      // Execute normal drag-and-drop flow (when Copilot modal is not needed)
      await executeMoveLogic()
    },
    [
      addToast,
      clearSortedBy,
      getUpdateForGroupDropEvent,
      groupByFieldId,
      groupedItems.allItemsByVerticalGroup,
      handleGroupDropRequestError,
      memex_group_by_multi_value_changes,
      postStats,
      sorts,
      tryOpenCopilotModal,
      updateAndReorderItem,
      hasIssueFieldVerticalGroup,
      validateUpdateForGroup,
      workflowsUsingColumnOption,
    ],
  )

  const moveCardToPosition = useCallback(
    async (cardToMoveId: number, verticalGroupId: string, position: CardPositionType) => {
      // Find the first/last item in the current column
      const verticalGroup = groupedItems.allItemsByVerticalGroup[verticalGroupId]
      const columnItems = verticalGroup?.items
      const item = position === CardPositionType.TOP ? columnItems?.at(0) : columnItems?.at(-1)
      if (!item) return

      const reorderData = {
        overItemId: item.id,
        side: position === CardPositionType.TOP ? DropSide.BEFORE : DropSide.AFTER,
        overGroupId: verticalGroup?.groupId,
      }
      const updateItemData = buildUpdateItemActions(undefined, reorderData)
      if (!updateItemData) {
        return
      }
      await updateAndReorderItem(cardToMoveId, updateItemData, {})
    },
    [groupedItems.allItemsByVerticalGroup, updateAndReorderItem],
  )

  const moveCards = useCallback(
    async (
      selectedItems: Array<MemexItemModel>,
      newVerticalGroup: VerticalGroup,
      dragHorizontalGroup?: HorizontalGroup,
      dropHorizontalGroup?: HorizontalGroup,
    ) => {
      const allItemsInTargetColumn = isEveryItemInColumn(queryClient, selectedItems, newVerticalGroup)

      // skip if all the items are already in the target column and there is no secondary grouping
      if (!horizontalGroupByFieldId && allItemsInTargetColumn) {
        return
      }

      const executeMoveLogic = async (): Promise<void> => {
        const itemUpdates = []
        let horizontalGroupActionFailed = false

        for (const item of selectedItems) {
          const updateColumnActions: Array<UpdateColumnValueAction> = []

          if (dragHorizontalGroup?.sourceObject && dropHorizontalGroup?.sourceObject) {
            // Multi-select swimlanes can't be regrouped by drag-and-drop, so a cross-swimlane drop
            // is a full no-op rather than reordering the cards without changing their value.
            if (dropHorizontalGroup.sourceObject.dataType === MemexColumnDataType.MultiSelect) {
              // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
              addToast({
                message: Resources.cannotDragForMultiSelectGroupMessage,
                type: ToastType.warning,
              })
              return
            }
            // Check if this is a Copilot assignment - if so, include the actor in the assignees
            if (shouldOpenCopilotModal([item], dropHorizontalGroup.sourceObject)) {
              const copilotAssigneeAction = getCopilotAssigneeAction({
                targetAssignees: dropHorizontalGroup.sourceObject.value as Array<IAssignee>,
                existingAssignees: item.columns.Assignees ?? [],
                keepExistingAssignees: memex_group_by_multi_value_changes ?? false,
              })
              updateColumnActions.push(copilotAssigneeAction)
            } else {
              const columnAction = await getUpdateForGroupDropEvent({
                activeItem: item,
                activeItemGroup: dragHorizontalGroup,
                overItemGroup: dropHorizontalGroup,
              })

              if (columnAction) {
                updateColumnActions.push(columnAction)
              } else {
                horizontalGroupActionFailed = true
                break
              }
            }
          }

          if (newVerticalGroup && !horizontalGroupActionFailed) {
            let nextAction: UpdateColumnValueAction | undefined = {
              dataType: MemexColumnDataType.SingleSelect,
              memexProjectColumnId: groupByFieldId,
              value: {id: newVerticalGroup.groupMetadata?.id ?? ''},
              isIssueField: hasIssueFieldVerticalGroup,
            }

            nextAction = await validateUpdateForGroup(item, nextAction)

            if (nextAction) {
              updateColumnActions.push(nextAction)
            } else {
              return
            }
          }

          itemUpdates.push({
            itemId: item.id,
            updates: updateColumnActions,
          })
        }

        if (!horizontalGroupActionFailed) {
          let rollbackData: OptimisticUpdateRollbackData | undefined = undefined
          try {
            const newGroupId = getServerGroupIdForVerticalGroupId(queryClient, newVerticalGroup.id)
            if (newGroupId) {
              const newSecondaryGroupId = hasServerGroupId(dropHorizontalGroup)
                ? dropHorizontalGroup.serverGroupId
                : undefined
              rollbackData = updateGroupForMemexItems(queryClient, selectedItems, newGroupId, newSecondaryGroupId)
            }
            await bulkUpdateMultipleColumnValues(itemUpdates)
          } catch (error) {
            handleGroupDropRequestError(error)
            if (rollbackData) {
              rollbackMemexItemData(queryClient, rollbackData)
            }
          }
        }
      }

      // Check if we should open the Copilot assignment modal for any of the selected items
      const modalOpened = dropHorizontalGroup?.sourceObject
        ? tryOpenCopilotModal(selectedItems, dropHorizontalGroup.sourceObject, executeMoveLogic)
        : false
      if (modalOpened) {
        return
      }

      // Execute normal drag-and-drop flow (when Copilot modal is not needed)
      await executeMoveLogic()
    },
    [
      bulkUpdateMultipleColumnValues,
      getUpdateForGroupDropEvent,
      groupByFieldId,
      handleGroupDropRequestError,
      horizontalGroupByFieldId,
      memex_group_by_multi_value_changes,
      queryClient,
      tryOpenCopilotModal,
      hasIssueFieldVerticalGroup,
      validateUpdateForGroup,
      addToast,
    ],
  )

  const boardCardActionsContextValue = useMemo(
    () => ({moveCard, moveCards, moveCardToPosition}),
    [moveCard, moveCardToPosition, moveCards],
  )

  return <BoardCardActionsContext value={boardCardActionsContextValue}>{children}</BoardCardActionsContext>
}

export function useBoardCardActions() {
  const value = use(BoardCardActionsContext)

  if (value == null) {
    throw new Error(`useBoardCardActions must be used within a BoardCardActionsContext`)
  }

  return value
}

/**
 * Checks whether every item is already in the target group
 */
export function isEveryItemInColumn(
  queryClient: QueryClient,
  selectedItems: Array<MemexItemModel>,
  targetVerticalGroup: VerticalGroup,
) {
  const targetServerGroupId = getServerGroupIdForVerticalGroupId(queryClient, targetVerticalGroup.id)
  if (!targetServerGroupId) {
    return false
  }

  // Return false if any of the items are not in the target group
  return selectedItems.every(item => {
    return isMemexItemInGroup(queryClient, item.id, targetServerGroupId)
  })
}
